import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import HealthcomLoadingPage from './HealthcomLoadingPage';

/**
 * App-level loading overlay. Sits above the route tree so the
 * loader's React instance stays mounted across boundaries that
 * would otherwise unmount/remount it (e.g. RootLanding's auth-check
 * loader handing off to Container's portal-fetch loader). A
 * remount restarts every CSS keyframe from 0%, which reads as a
 * mid-load glitch — keeping a stable mount point is the only way
 * to make the animation feel continuous through the entire flow.
 *
 * Multiple consumers can register concurrently via
 * `useLoadingOverlay(loading)`. The overlay stays visible as long
 * as ANY consumer is loading, so the typical handoff
 *   RootLanding (loading=true → false)
 *   Container   (loading=false → true)
 * leaves at least one true entry in the map at every render and
 * the loader never animates out.
 */

type Phase = 'visible' | 'leaving' | 'gone';

type Ctx = {
  registerLoading: (id: string, loading: boolean) => void;
  phase: Phase;
};

const LoadingOverlayContext = createContext<Ctx>({
  registerLoading: () => {},
  phase: 'gone',
});

/**
 * Register the calling component as a loading consumer. While
 * `loading` is true, the overlay stays engaged. On unmount the
 * registration is cleared automatically.
 */
export const useLoadingOverlay = (loading: boolean): void => {
  const id = useId();
  const { registerLoading } = useContext(LoadingOverlayContext);

  useEffect(() => {
    registerLoading(id, loading);

    return () => {
      registerLoading(id, false);
    };
  }, [id, loading, registerLoading]);
};

/**
 * Read the overlay's current phase. Useful for paired entrance
 * animations on portal/login content (e.g. fading in while the
 * overlay fades out).
 */
export const useLoadingOverlayPhase = (): Phase => useContext(LoadingOverlayContext).phase;

type ProviderProps = {
  children: React.ReactNode;
};

export const LoadingOverlayProvider: React.FC<ProviderProps> = ({ children }) => {
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [phase, setPhase] = useState<Phase>('visible');

  const registerLoading = useCallback((id: string, loading: boolean) => {
    setLoadingMap(prev => {
      if (prev[id] === loading) return prev;
      const next = { ...prev };

      if (loading) next[id] = true;
      else delete next[id];

      return next;
    });
  }, []);

  const anyLoading = useMemo(() => Object.values(loadingMap).some(Boolean), [loadingMap]);

  // Three-phase state machine, identical in spirit to the prior
  // Container-local one, but lifted up so it survives the
  // RootLanding → Container handoff.
  //
  // Settle delay: don't commit `visible → leaving` until anyLoading
  // has been false for SETTLE_MS continuously. This absorbs brief
  // windows where one consumer's HTTP chain finishes and another
  // hasn't kicked off yet.
  useEffect(() => {
    const SETTLE_MS = 400;
    const FADE_MS = 550;

    if (anyLoading) {
      if (phase !== 'visible') setPhase('visible');

      return;
    }

    if (phase === 'visible') {
      const t = setTimeout(() => setPhase('leaving'), SETTLE_MS);

      return () => clearTimeout(t);
    }

    if (phase === 'leaving') {
      const t = setTimeout(() => setPhase('gone'), FADE_MS);

      return () => clearTimeout(t);
    }
  }, [anyLoading, phase]);

  const ctxValue = useMemo<Ctx>(() => ({ registerLoading, phase }), [registerLoading, phase]);

  return (
    <LoadingOverlayContext.Provider value={ctxValue}>
      {children}
      {phase !== 'gone' && (
        <div
          className={
            phase === 'leaving'
              ? 'hc-loader-fade-out fixed inset-0 z-[100] pointer-events-none'
              : 'fixed inset-0 z-[100]'
          }
          aria-hidden={phase === 'leaving' ? true : undefined}
        >
          <HealthcomLoadingPage />
        </div>
      )}
    </LoadingOverlayContext.Provider>
  );
};
